import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import "regenerator-runtime/runtime.js";
import { parseRetryAfterHeader, logger, forEachEnvelopeItem } from '@sentry/utils';
var MIN_DELAY = 100; // 100 ms
var START_DELAY = 5000; // 5 seconds
var MAX_DELAY = 3.6e6; // 1 hour

function isReplayEnvelope(envelope) {
  var isReplay = false;
  forEachEnvelopeItem(envelope, function (_, type) {
    if (type === 'replay_event') {
      isReplay = true;
    }
  });
  return isReplay;
}
function log(msg, error) {
  (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.info("[Offline]: ".concat(msg), error);
}

/**
 * Wraps a transport and stores and retries events when they fail to send.
 *
 * @param createTransport The transport to wrap.
 */
function makeOfflineTransport(createTransport) {
  return function (options) {
    var transport = createTransport(options);
    var store = options.createStore ? options.createStore(options) : undefined;
    var retryDelay = START_DELAY;
    var flushTimer;
    function shouldQueue(env, error, retryDelay) {
      // We don't queue Session Replay envelopes because they are:
      // - Ordered and Replay relies on the response status to know when they're successfully sent.
      // - Likely to fill the queue quickly and block other events from being sent.
      if (isReplayEnvelope(env)) {
        return false;
      }
      if (options.shouldStore) {
        return options.shouldStore(env, error, retryDelay);
      }
      return true;
    }
    function flushIn(delay) {
      if (!store) {
        return;
      }
      if (flushTimer) {
        clearTimeout(flushTimer);
      }
      flushTimer = setTimeout( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
        var found;
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              flushTimer = undefined;
              _context.next = 3;
              return store.pop();
            case 3:
              found = _context.sent;
              if (found) {
                log('Attempting to send previously queued event');
                void send(found).catch(function (e) {
                  log('Failed to retry sending', e);
                });
              }
            case 5:
            case "end":
              return _context.stop();
          }
        }, _callee);
      })), delay);

      // We need to unref the timer in node.js, otherwise the node process never exit.
      if (typeof flushTimer !== 'number' && flushTimer.unref) {
        flushTimer.unref();
      }
    }
    function flushWithBackOff() {
      if (flushTimer) {
        return;
      }
      flushIn(retryDelay);
      retryDelay = Math.min(retryDelay * 2, MAX_DELAY);
    }
    function send(_x) {
      return _send.apply(this, arguments);
    }
    function _send() {
      _send = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(envelope) {
        var result, delay;
        return regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              _context2.prev = 0;
              _context2.next = 3;
              return transport.send(envelope);
            case 3:
              result = _context2.sent;
              delay = MIN_DELAY;
              if (!result) {
                _context2.next = 12;
                break;
              }
              if (!(result.headers && result.headers['retry-after'])) {
                _context2.next = 10;
                break;
              }
              delay = parseRetryAfterHeader(result.headers['retry-after']);
              _context2.next = 12;
              break;
            case 10:
              if (!((result.statusCode || 0) >= 400)) {
                _context2.next = 12;
                break;
              }
              return _context2.abrupt("return", result);
            case 12:
              flushIn(delay);
              retryDelay = START_DELAY;
              return _context2.abrupt("return", result);
            case 17:
              _context2.prev = 17;
              _context2.t0 = _context2["catch"](0);
              _context2.t1 = store;
              if (!_context2.t1) {
                _context2.next = 24;
                break;
              }
              _context2.next = 23;
              return shouldQueue(envelope, _context2.t0, retryDelay);
            case 23:
              _context2.t1 = _context2.sent;
            case 24:
              if (!_context2.t1) {
                _context2.next = 32;
                break;
              }
              _context2.next = 27;
              return store.insert(envelope);
            case 27:
              flushWithBackOff();
              log('Error sending. Event queued', _context2.t0);
              return _context2.abrupt("return", {});
            case 32:
              throw _context2.t0;
            case 33:
            case "end":
              return _context2.stop();
          }
        }, _callee2, null, [[0, 17]]);
      }));
      return _send.apply(this, arguments);
    }
    if (options.flushAtStartup) {
      flushWithBackOff();
    }
    return {
      send: send,
      flush: function flush(t) {
        return transport.flush(t);
      }
    };
  };
}
export { MIN_DELAY, START_DELAY, makeOfflineTransport };